import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../layouts/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { paymentsAPI, dropdownAPI } from '../lib/api';
import { formatDate, formatCurrency, getStatusClass } from '../lib/utils';
import {
  CreditCard,
  Eye,
  Loader2,
  Calendar,
  MapPin,
  User,
  CheckCircle
} from 'lucide-react';

export default function PendingPayments() {
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [paymentModes, setPaymentModes] = useState([]);
  const [payDialog, setPayDialog] = useState({ open: false, claim: null });
  const [paymentData, setPaymentData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: '',
    payment_amount: 0
  });

  useEffect(() => {
    fetchPendingPayments();
    fetchPaymentModes();
  }, []);

  const fetchPendingPayments = async () => {
    setLoading(true);
    try {
      const response = await paymentsAPI.getPending();
      setClaims(response.data);
    } catch (error) {
      toast.error('Failed to load pending payments');
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentModes = async () => {
    try {
      const response = await dropdownAPI.getPaymentModes();
      setPaymentModes(response.data);
    } catch (error) {
      console.error('Failed to fetch payment modes');
    }
  };

  const openPayDialog = (claim) => {
    setPayDialog({ open: true, claim });
    setPaymentData({
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: '',
      payment_amount: claim.calculations?.advance_claim_amount || 0
    });
  };

  const handlePayment = async () => {
    if (!paymentData.payment_mode) {
      toast.error('Please select payment mode');
      return;
    }

    setActionLoading(payDialog.claim.id);
    try {
      await paymentsAPI.processPayment(payDialog.claim.id, paymentData);
      toast.success('Payment processed successfully');
      setPayDialog({ open: false, claim: null });
      fetchPendingPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="pending-payments-page">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Pending Payments</h1>
          <p className="text-muted-foreground">Process payments for approved advance claims</p>
        </div>

        {/* Claims List */}
        {claims.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <CheckCircle className="w-12 h-12 text-green-500 mb-4" />
              <h3 className="text-lg font-medium">All payments processed!</h3>
              <p className="text-muted-foreground">No claims pending payment</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {claims.map((claim) => (
              <Card key={claim.id} className="card-hover" data-testid={`payment-card-${claim.claim_number}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    {/* Claim Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">{claim.claim_number}</h3>
                        <Badge className={getStatusClass(claim.status)}>
                          {claim.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-2">
                        <span className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          {claim.user_name} ({claim.user_position})
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(claim.date_of_travel)}
                        </span>
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {claim.project_site_location}
                        </span>
                      </div>
                      {claim.approved_by && (
                        <p className="text-sm text-green-600">
                          Approved by {claim.approved_by} on {formatDate(claim.approval_date)}
                        </p>
                      )}
                    </div>

                    {/* Amount */}
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Amount to Pay</p>
                      <p className="text-xl font-bold text-primary">
                        {formatCurrency(claim.calculations?.advance_claim_amount)}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/claims/${claim.id}`)}
                        data-testid={`view-payment-${claim.claim_number}`}
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openPayDialog(claim)}
                        disabled={actionLoading === claim.id}
                        data-testid={`process-payment-${claim.claim_number}`}
                      >
                        {actionLoading === claim.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <CreditCard className="w-4 h-4 mr-1" />
                            Process Payment
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Payment Dialog */}
        <Dialog open={payDialog.open} onOpenChange={(open) => setPayDialog({ ...payDialog, open })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Process Payment</DialogTitle>
              <DialogDescription>
                Enter payment details for claim {payDialog.claim?.claim_number}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Payment Date</Label>
                <Input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                  data-testid="payment-date-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <Select
                  value={paymentData.payment_mode}
                  onValueChange={(value) => setPaymentData({ ...paymentData, payment_mode: value })}
                >
                  <SelectTrigger data-testid="payment-mode-select">
                    <SelectValue placeholder="Select payment mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {paymentModes.map((mode) => (
                      <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Payment Amount</Label>
                <Input
                  type="number"
                  value={paymentData.payment_amount}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_amount: parseFloat(e.target.value) || 0 })}
                  data-testid="payment-amount-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setPayDialog({ open: false, claim: null })}>
                Cancel
              </Button>
              <Button
                onClick={handlePayment}
                disabled={actionLoading}
                data-testid="confirm-payment-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                Confirm Payment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
